/**
 * BooleanNode - CSG boolean operations on geometries
 *
 * SceneNode calls performBooleanOp(geoA, geoB) with upstream geometries.
 * Uses three-bvh-csg library.
 */

import { BaseNode } from '@nodes/BaseNode';
import { internals, params } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { ThreeDependencies } from '../threejsPlugin';
import type { Three, BufferGeometry } from '../types';
import { Evaluator, Brush, ADDITION, SUBTRACTION, INTERSECTION } from 'three-bvh-csg';

const booleanEndpoints = {
  ...internals({
    geometryA: { dataType: 'three:geometry', isInput: true, label: 'Geometry A' },
    geometryB: { dataType: 'three:geometry', isInput: true, label: 'Geometry B' },
    geometryOut: { dataType: 'three:geometry', isOutput: true }
  }),
  ...params({
    operation: {
      dataType: 'string',
      value: 'subtract',
      options: ['union', 'subtract', 'intersect'] as const,
      label: 'Operation'
    }
  })
};

class BooleanNode extends BaseNode<typeof booleanEndpoints> {
  private three: Three;
  private evaluator: Evaluator;
  /** Callback to request scene rebuild */
  private rebuildCallback: (() => void) | null = null;

  constructor(id: string, deps: ThreeDependencies) {
    super(id, deps);
    this.three = deps.three;
    this.evaluator = new Evaluator();
  }

  /** Perform boolean operation on two geometries — called by SceneNode */
  performBooleanOp(geoA: BufferGeometry | null, geoB: BufferGeometry | null): BufferGeometry | null {
    if (!geoA || !geoB) {
      // If we have A but not B, pass through A
      return geoA ? geoA.clone() : null;
    }

    try {
      const brushA = new Brush(geoA);
      const brushB = new Brush(geoB);

      let operation;
      switch (this.params.operation.value) {
        case 'union': operation = ADDITION; break;
        case 'subtract': operation = SUBTRACTION; break;
        case 'intersect': operation = INTERSECTION; break;
        default: operation = SUBTRACTION;
      }

      const result = this.evaluator.evaluate(brushA, brushB, operation);
      const geometry = result.geometry as BufferGeometry;
      geometry.computeVertexNormals();
      return geometry;
    } catch (err) {
      this.logError('Boolean operation failed:', err);
      return geoA?.clone() ?? null;
    }
  }

  /** Store rebuild callback from SceneNode */
  setRebuildCallback(callback: () => void): void {
    this.rebuildCallback = callback;
  }

  onParamChanged(): void {
    this.rebuildCallback?.();
  }
}

export const booleanNodeDef: NodeDef<BooleanNode> = {
  type: 'Boolean',
  shortName: 'bool',
  info: 'CSG boolean operations (union, subtract, intersect) on two geometries.',
  category: 'geometry',
  endpoints: booleanEndpoints,
  ctor: BooleanNode
};
